/*
  industrialkonzept Next, part 2 (2026-09-23)
  Cart: ideas for an empty cart (a full cart gets no suggestions, on purpose).
  Main menu: opens on hover with a mouse. Country lists open at the current country.
  Legal texts stored as one block get headings, paragraphs and a linked table of contents.
  Product pages: "More by the designer" is filled when it comes near the screen.
  Optional: without this script everything works as before.
*/
(() => {
  if (window.__ikNextUi) return;
  window.__ikNextUi = true;

  const T = (k, v) => (window.ikT ? window.ikT(k, v) : k);
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const t = {
    empty: T('nextui.empty'),
    newIn: T('nextui.new_in'),
    favourites: T('nextui.favourites'),
    lighting: T('nextui.lighting'),
    chairs: T('nextui.chairs'),
    tables: T('nextui.tables'),
    storage: T('nextui.shelves'),
  };

  /* ---------- Cart drawer extras ---------- */
  // The drawer is created when it opens for the first time, so look it up every time
  const getDrawer = () => document.querySelector('[data-cart-drawer]');
  let observed = null;
  let busy = false;
  let lastKey = '';
  let shown = false; // our block is in the drawer
  let swapping = false; // we are changing the drawer ourselves, the watcher looks away

  const footerOf = (el) => el.querySelector(':scope > .mt-auto') || el.lastElementChild;

  const renderEmpty = () => {
    const links = [
      [t.newIn, 'collections/new-arrivals'],
      [t.favourites, 'collections/current-favourites'],
      [t.lighting, 'collections/table-lamps'],
      [t.chairs, 'collections/chairs-armchairs'],
      [t.tables, 'collections/dining-tables'],
      [t.storage, 'collections/shelves'],
    ];
    return (
      '<div class="ikn-cartx" data-ikn-cartx="empty"><div class="ikn-cartx__head"><span class="ikh-label">' + esc(t.empty) + '</span></div>' +
      '<div class="ikn-cartx__chips">' +
      links.map(([label, path]) => '<a class="ikn-chip" href="' + esc(base + path) + '">' + esc(label) + '</a>').join('') +
      '</div></div>'
    );
  };

  const enhance = async () => {
    const drawer = getDrawer();
    if (!drawer || busy) return;
    watch(drawer);
    busy = true;
    try {
      const cart = await fetch(base + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const key = cart.item_count ? 'items:' + cart.items.map((i) => i.product_id).join(',') : 'empty';
      const present = drawer.querySelector('[data-ikn-cartx]');
      if (present && key === lastKey) return;
      lastKey = key;
      // only an empty cart gets ideas; a full one stays calm, no upselling
      const html = cart.item_count ? '' : renderEmpty();
      // Swap in one go once the new block is ready, so nothing flickers in between
      swapping = true;
      const old = drawer.querySelector('[data-ikn-cartx]');
      if (old) old.remove();
      if (html) footerOf(drawer).insertAdjacentHTML('beforebegin', html);
      shown = !!html;
    } catch (e) {
      /* the cart itself keeps working */
    } finally {
      busy = false;
      window.setTimeout(() => {
        swapping = false;
      }, 0);
    }
  };

  // The theme re-renders parts of the drawer; put the block back if it disappears
  const watch = (drawer) => {
    if (observed === drawer) return;
    observed = drawer;
    new MutationObserver(() => {
      // Only when the theme took our block away (not our own swap, not when there was none)
      if (swapping || busy || !shown) return;
      if (!drawer.querySelector('[data-ikn-cartx]')) {
        shown = false;
        lastKey = '';
        window.clearTimeout(drawer.__iknTimer);
        drawer.__iknTimer = window.setTimeout(enhance, 400);
      }
    }).observe(drawer, { childList: true, subtree: true });
  };

  ['cart-is-open', 'baseline:cart:update', 'baseline:cart:afteradditem'].forEach((name) => {
    window.addEventListener(name, () => window.setTimeout(enhance, 350));
  });
  document.addEventListener(
    'click',
    (e) => {
      if (e.target.closest('[data-cart-drawer-toggle]')) window.setTimeout(enhance, 500);
    },
    true
  );

  /* Legal texts: some policies are stored as one long block with line breaks ("1) Scope<br>1.1 …<br>1.2 …").
     Here they get the structure the text already has: "1) …" becomes a heading, every line its own
     paragraph with its clause number set apart, and the table of contents links to the headings.
     Only the markup changes: every word and its order stay exactly as written in Shopify. */
  document.querySelectorAll('.ikn-legal .shopify-policy__body .rte').forEach((rte) => {
    const HEAD = /^(\d{1,2})\)\s+\S/;
    const TOC = /^(table of contents|contents|inhaltsübersicht|inhaltsverzeichnis|inhalt)\s*:?$/i;
    const clean = (t) => t.replace(/\u00a0/g, ' ').trim();
    [...rte.children].forEach((p) => {
      if (p.tagName !== 'P') return;
      if (!clean(p.textContent) && !p.querySelector('img')) {
        p.remove();
        return;
      }
      if (!p.querySelector(':scope > br')) return;
      const lines = [[]];
      [...p.childNodes].forEach((n) => (n.nodeName === 'BR' ? lines.push([]) : lines[lines.length - 1].push(n)));
      const out = document.createDocumentFragment();
      let toc = null;
      lines.forEach((nodes) => {
        const text = clean(nodes.map((n) => n.textContent).join(''));
        if (!text) {
          if (toc && toc.children.length) toc = null;
          return;
        }
        if (TOC.test(text)) {
          const head = document.createElement('p');
          head.className = 'ikn-legal-toc__head';
          nodes.forEach((n) => head.appendChild(n));
          toc = document.createElement('ol');
          toc.className = 'ikn-legal-toc';
          out.append(head, toc);
          return;
        }
        const item = text.match(/^(\d{1,2})\.\s+\S/);
        if (toc && item && text.length < 90) {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = '#ikn-s' + item[1];
          nodes.forEach((n) => a.appendChild(n));
          li.appendChild(a);
          toc.appendChild(li);
          return;
        }
        toc = null;
        const m = text.match(HEAD);
        const el = document.createElement(m ? 'h2' : 'p');
        if (m) el.id = 'ikn-s' + m[1];
        nodes.forEach((n) => el.appendChild(n));
        // "1.1 …": the clause number set apart, quieter
        const first = el.firstChild;
        const no = !m && first && first.nodeType === 3 && first.textContent.match(/^\s*(\d{1,2}(\.\d{1,2})+)\s+/);
        if (no) {
          first.textContent = first.textContent.slice(no[0].length);
          const span = document.createElement('span');
          span.className = 'ikn-legal__no';
          span.textContent = no[1];
          el.insertBefore(span, first);
        }
        out.appendChild(el);
      });
      p.replaceWith(out);
    });
    // headings are only linked when the page has them
    rte.querySelectorAll('.ikn-legal-toc a').forEach((a) => {
      if (!document.getElementById(a.getAttribute('href').slice(1))) a.removeAttribute('href');
    });
  });

  /* Country lists (header, phone menu, footer) open at the country you are in, not at the top of 240 */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('button[aria-controls^="country-list-"]');
    if (!b) return;
    window.setTimeout(() => {
      const list = document.getElementById(b.getAttribute('aria-controls'));
      const here = list && list.querySelector('[aria-current]');
      if (here) list.scrollTop = here.closest('li').offsetTop - list.clientHeight / 2 + here.closest('li').offsetHeight / 2;
    }, 30);
  });

  /* Main menu opens on hover (mouse only; touch screens and the keyboard keep the theme's click).
     The theme's own open state (Alpine "expanded") is switched, so it looks and closes as before.
     A short wait before opening keeps it from flashing when the mouse only passes by; moving from one
     menu to the next switches at once, leaving closes after a moment so the gap to the panel is safe.
     A click on an already open menu keeps it open instead of shutting it again. */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const menus = [...document.querySelectorAll('header data-island')].filter((i) => i.querySelector(':scope > button[aria-controls^="menu"]'));
    const state = (i) => window.Alpine && window.Alpine.$data(i);
    const isOpen = (i) => { const d = state(i); return !!(d && d.expanded); };
    const set = (i, v) => { const d = state(i); if (d && d.expanded !== v) d.expanded = v; };
    let openT = 0, closeT = 0;
    menus.forEach((i) => {
      i.addEventListener('mouseenter', () => {
        window.clearTimeout(closeT);
        window.clearTimeout(openT);
        const switching = menus.some((o) => o !== i && isOpen(o));
        openT = window.setTimeout(() => {
          menus.forEach((o) => o !== i && set(o, false));
          set(i, true);
        }, switching ? 0 : 120);
      });
      i.addEventListener('mouseleave', () => {
        window.clearTimeout(openT);
        closeT = window.setTimeout(() => set(i, false), 200);
      });
      i.addEventListener('click', (e) => {
        if (e.detail === 0) return; // Enter / Space from the keyboard: the theme's own open and close
        const b = e.target.closest('button');
        if (b && b.parentElement === i && isOpen(i)) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, true);
    });
  }
  /* Editorials on phones: the magnifier in the header (sections/header.liquid) opens the same search bar
     as on every other page, right under the header row, and puts the cursor in it; a second tap closes it */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ik-search-toggle]');
    if (!btn) return;
    const row = document.getElementById(btn.getAttribute('aria-controls'));
    if (!row) return;
    const open = row.hidden;
    row.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    if (open) {
      const input = row.querySelector('input[type="search"]');
      if (input) input.focus();
    }
  });

  // The shop's customer reviews (REVIEWS.io, public numbers, kept for a day in this tab): service lines on
  // product pages ([data-ikn-rating]) and the line under checkout in the cart (snippets/cart-footer.liquid).
  // The cart redraws its footer on every change and the drawer only exists once it opens, so the text also
  // goes into --ikn-rating on <html> with html[data-ikn-rated]; the cart line shows it by CSS (assets/ik-cart.css)
  const rating = (() => {
    let data = null;
    let asked = false;
    try {
      const c = JSON.parse(sessionStorage.getItem('ikn-rating') || 'null');
      if (c && Date.now() - c.t < 864e5) data = c;
    } catch (e) {}
    const paint = () => {
      if (!data || !data.count || data.avg < 4) return;
      const src = document.querySelector('[data-ikn-rating-text]');
      if (!src) return;
      const lang = document.documentElement.lang || undefined;
      const text = src
        .getAttribute('data-ikn-rating-text')
        .replace('%r', new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(data.avg))
        .replace('%c', new Intl.NumberFormat(lang).format(data.count));
      document.querySelectorAll('[data-ikn-rating]').forEach((box) => {
        const out = box.querySelector('[data-ikn-rating-out]');
        if (out) out.textContent = text;
        box.hidden = false;
      });
      document.documentElement.style.setProperty('--ikn-rating', JSON.stringify(text));
      document.documentElement.setAttribute('data-ikn-rated', '');
    };
    return () => {
      if (data) return paint();
      if (asked) return;
      asked = true;
      fetch('https://api.reviews.io/merchant/reviews?store=andustrialkonzept.com&per_page=1')
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          if (!j || !j.stats) return;
          data = { avg: parseFloat(j.stats.average_rating), count: parseInt(j.stats.total_reviews, 10), t: Date.now() };
          try {
            sessionStorage.setItem('ikn-rating', JSON.stringify(data));
          } catch (e) {}
          paint();
        })
        .catch(() => {});
    };
  })();
  // asked for only where it shows: a page with the service lines or the cart page at once, the drawer when it opens
  if (document.querySelector('[data-ikn-rating], [data-ikn-carttrust]')) rating();
  window.addEventListener('cart-is-open', rating);

  // Energy label inside a card that is itself a link (snippets/ik-energy-label.liquid, nested): opens the PDF in a new tab
  const energyOpen = (e) => {
    const el = e.target.closest && e.target.closest('[data-ikn-energy-href]');
    if (!el) return;
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    window.open(el.getAttribute('data-ikn-energy-href'), '_blank', 'noopener');
  };
  document.addEventListener('click', energyOpen, true);
  document.addEventListener('keydown', energyOpen, true);
  // EU legal guarantee notice: the line opens the official notice in a sheet (snippets/ik-product-service.liquid)
  // Capture phase: the cart drawer (#right-drawer-slot) stops clicks from bubbling
  document.addEventListener(
    'click',
    (e) => {
      const open = e.target.closest('[data-ikn-eu-open]');
      if (open) {
        const d = (open.closest('[data-ikn-eu-host]') || open.closest('li')).querySelector('[data-ikn-eu]');
        if (d && d.showModal) d.showModal();
        return;
      }
      const d = e.target.closest('[data-ikn-eu]');
      if (d && (e.target.closest('[data-ikn-eu-close]') || e.target === d)) d.close();
    },
    true
  );
  // Escape closes only the sheet, not the cart drawer behind it
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape') return;
      const d = document.querySelector('[data-ikn-eu][open]');
      if (!d) return;
      e.preventDefault();
      e.stopPropagation();
      d.close();
    },
    true
  );

  // GARAN label: /products/…#garan (link in the order confirmation) opens it straight away
  if (location.hash === '#garan') {
    const g = document.querySelector('[data-ikn-garan]');
    if (g && g.showModal) g.showModal();
  }

  // Contact form: a question from a product or brand page arrives with its subject already written
  // (?topic=… from snippets/ik-product-service.liquid and ik-collection-header.liquid)
  const topic = new URL(location.href).searchParams.get('topic');
  const body = document.getElementById('ContactFormMessage');
  if (topic && body && !body.value.trim()) body.value = topic.slice(0, 300) + '\n\n';

  // Product notes: a brand's discount code is copied with one tap (snippets/ik-product-notes.liquid)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ikn-copy]');
    if (!btn || !navigator.clipboard) return;
    const label = btn.querySelector('[data-ikn-copy-label]');
    const code = btn.getAttribute('data-ikn-copy');
    navigator.clipboard.writeText(code).then(() => {
      btn.classList.add('is-copied');
      if (label) label.textContent = btn.getAttribute('data-ikn-copied') || code;
      window.setTimeout(() => {
        btn.classList.remove('is-copied');
        if (label) label.textContent = code;
      }, 1800);
    }).catch(() => {});
  });

  // Product pages: "More by the designer" (snippets/ik-product-extras.liquid). The cards come from the
  // designer page itself (templates/collection.ikn-cards.liquid), this product left out; the rail stays
  // hidden when the designer has nothing else in the shop.
  document.querySelectorAll('[data-ikn-designer-rail]').forEach((rail) => {
    const track = rail.querySelector('[data-ikn-designer-track]');
    if (!track) return;
    const load = () => {
      const url = rail.getAttribute('data-ikn-designer-rail') + '&view=ikn-cards';
      fetch(url, { headers: { Accept: 'text/html' } })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const box = document.createElement('div');
          box.innerHTML = html;
          const self = rail.getAttribute('data-ikn-self');
          box.querySelectorAll('a.ikh-card').forEach((a) => {
            if ((a.getAttribute('href') || '').split('?')[0] === self) a.remove();
          });
          const cards = box.querySelectorAll('a.ikh-card');
          if (!cards.length) return rail.remove();
          track.replaceChildren(...[...cards].slice(0, 16));
          rail.hidden = false;
          // rails measure themselves on resize (assets/ik-home.js)
          window.dispatchEvent(new Event('resize'));
        })
        .catch(() => rail.remove());
    };
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        load();
      }
    }, { rootMargin: '600px 0px' });
    // a hidden element never intersects: watch the section just before it
    io.observe(rail.previousElementSibling || rail.parentElement);
  });
})();

/* Phone menu button, Safari on iPhone (2026-09-27): when the page opens with a restored scroll position,
   draw the button once more after the first frames, in case Safari skipped it (see ik-next-ui.css). */
(() => {
  const nudge = () => {
    if (window.scrollY < 1) return;
    document.querySelectorAll('nav[aria-label="Header"] > div > div:first-child > button').forEach((b) => {
      b.style.opacity = '0.999';
      requestAnimationFrame(() => requestAnimationFrame(() => (b.style.opacity = '')));
    });
  };
  window.addEventListener('pageshow', () => setTimeout(nudge, 60));
  window.addEventListener('load', () => setTimeout(nudge, 300), { once: true });
})();

/* Pictures further down (2026-09-27): the theme marks them loading="lazy", and Safari only starts such a
   picture just before it comes into view, so while scrolling (and in the sideways product rows) pictures
   appeared late. Once the page has fully loaded, every section that comes within about one and a half
   screens gets its pictures loaded right away (up to 16), the cards further right in a row included. The first view
   of the page is untouched, so its speed stays the same. */
(() => {
  const main = document.getElementById('MainContent');
  if (!main || !('IntersectionObserver' in window)) return;
  // at most 16 per section: covers every row on the home page, while a long collection grid keeps loading
  // the rest as before
  // only pictures that are actually shown (a hidden one, such as the hover picture on phones, stays unloaded)
  const warm = (sec) =>
    [...sec.querySelectorAll('img[loading="lazy"]')]
      .filter((img) => img.getClientRects().length)
      .slice(0, 16)
      .forEach((img) => (img.loading = 'eager'));
  const start = () => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        warm(e.target);
      });
    }, { rootMargin: '1200px 0px' });
    main.querySelectorAll(':scope > .shopify-section').forEach((sec) => io.observe(sec));
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
